"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import type { PlanId } from "@/lib/branchTypes"
import type { LossOfControlBoundsForPlan } from "./types"
import { defaultGraphData } from "./graphConstants"

export type LossOfControlBound = 'min' | 'max'
export type LossOfControlByPlan = Record<PlanId, LossOfControlBoundsForPlan>

type LossOfControlEditorContextValue = {
  editorMode: boolean
  setEditorMode: (next: boolean) => void
  lossOfControlByPlan: LossOfControlByPlan
  setLossOfControlValue: (
    plan: PlanId,
    bound: LossOfControlBound,
    dateString: string,
    value: number,
  ) => void
}

const LossOfControlEditorContext =
  createContext<LossOfControlEditorContextValue | null>(null)

const cloneLossOfControlByPlan = (input: LossOfControlByPlan): LossOfControlByPlan => {
  const planIds: PlanId[] = ['S', 'A', 'B', 'C', 'D']
  const clone = {} as LossOfControlByPlan
  for (const planId of planIds) {
    clone[planId] = {
      min: input[planId].min.map(datum => ({ ...datum })),
      max: input[planId].max.map(datum => ({ ...datum })),
    }
  }
  return clone
}

const persistLossOfControlData = async (
  lossOfControlByPlan: LossOfControlByPlan
) => {
  try {
    const response = await fetch("/api/loss-of-control-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lossOfControlByPlan }),
    })
    if (!response.ok) {
      console.error(
        "Failed to save loss-of-control data:",
        response.status,
      )
    }
  } catch (error) {
    console.error("Failed to save loss-of-control data:", error)
  }
}

export const LossOfControlEditorProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [editorMode, setEditorMode] = useState(false)
  const [lossOfControlByPlan, setLossOfControlByPlanState] = useState(
    () => cloneLossOfControlByPlan(defaultGraphData.lossOfControlByPlan)
  )

  const lossOfControlByPlanRef = useRef(lossOfControlByPlan)
  lossOfControlByPlanRef.current = lossOfControlByPlan
  const hasUnsavedEditsRef = useRef(false)
  const wasEditorModeOnRef = useRef(editorMode)

  // We only flush to disk when the user exits edit mode (or on unmount /
  // tab close) rather than after every drag. Writing the JSON file mid-drag
  // makes Next.js Fast-Refresh do a full reload, which scrolls the page
  // and resets edit state.
  useEffect(() => {
    const wasOn = wasEditorModeOnRef.current
    wasEditorModeOnRef.current = editorMode
    if (wasOn && !editorMode && hasUnsavedEditsRef.current) {
      hasUnsavedEditsRef.current = false
      void persistLossOfControlData(lossOfControlByPlanRef.current)
    }
  }, [editorMode])

  useEffect(() => {
    // sendBeacon is the only reliable way to persist during pagehide /
    // beforeunload; fetch() requests get cancelled when the document goes
    // away. We use pagehide because Safari is not guaranteed to fire
    // beforeunload.
    const flushOnPageHide = () => {
      if (!hasUnsavedEditsRef.current) return
      hasUnsavedEditsRef.current = false
      const payload = JSON.stringify({
        lossOfControlByPlan: lossOfControlByPlanRef.current,
      })
      const blob = new Blob([payload], { type: "application/json" })
      navigator.sendBeacon("/api/loss-of-control-data", blob)
    }
    window.addEventListener("pagehide", flushOnPageHide)
    return () => {
      window.removeEventListener("pagehide", flushOnPageHide)
      if (hasUnsavedEditsRef.current) {
        hasUnsavedEditsRef.current = false
        void persistLossOfControlData(lossOfControlByPlanRef.current)
      }
    }
  }, [])

  // Ctrl-E toggles the editor overlay. Using ctrl (not cmd) on mac because
  // cmd-E is intercepted by macOS / browsers for "Use Selection for Find"
  // in some apps. We bail out if the keypress originates inside a text
  // input so editor toggling can't hijack the shortcut when the user is
  // typing. We attach in `capture` phase so we fire before any other
  // window-level keydown listener (e.g. Playbook's other cmd-* shortcuts)
  // and call stopPropagation so the event can't be reinterpreted later in
  // the bubble phase.
  useEffect(() => {
    // Dev-only editor toggle: the curve edits persist by writing
    // extrapolatedGraphData.json into the checkout, so production never
    // registers the shortcut.
    if (process.env.NODE_ENV === 'production') return
    const handleKeydown = (event: KeyboardEvent) => {
      if (!event.ctrlKey) return
      if (event.metaKey || event.altKey || event.shiftKey) return
      if (event.key.toLowerCase() !== 'e') return
      const targetElement = event.target as HTMLElement | null
      const tagName = targetElement?.tagName
      if (tagName === 'INPUT' || tagName === 'TEXTAREA') return
      if (targetElement?.isContentEditable) return
      event.preventDefault()
      event.stopPropagation()
      setEditorMode(previous => {
        const next = !previous
        // Surface state on the window so the user can sanity-check the
        // shortcut from devtools (e.g. window.__lossOfControlEditorMode).
        if (typeof window !== 'undefined') {
          (window as unknown as { __lossOfControlEditorMode?: boolean })
            .__lossOfControlEditorMode = next
        }
        return next
      })
    }
    window.addEventListener('keydown', handleKeydown, { capture: true })
    return () =>
      window.removeEventListener('keydown', handleKeydown, { capture: true })
  }, [])

  const setLossOfControlValue = useCallback<
    LossOfControlEditorContextValue['setLossOfControlValue']
  >((plan, bound, dateString, value) => {
    setLossOfControlByPlanState(previous => {
      const previousBoundSeries = previous[plan][bound]
      const matchingPointIndex = previousBoundSeries.findIndex(
        datum => datum.date === dateString
      )
      if (matchingPointIndex === -1) return previous
      const previousValue = previousBoundSeries[matchingPointIndex].value
      if (previousValue === value) return previous
      const nextBoundSeries = previousBoundSeries.slice()
      nextBoundSeries[matchingPointIndex] = { date: dateString, value }
      hasUnsavedEditsRef.current = true
      return {
        ...previous,
        [plan]: {
          ...previous[plan],
          [bound]: nextBoundSeries,
        },
      }
    })
  }, [])

  const contextValue = useMemo<LossOfControlEditorContextValue>(
    () => ({
      editorMode,
      setEditorMode,
      lossOfControlByPlan,
      setLossOfControlValue,
    }),
    [editorMode, lossOfControlByPlan, setLossOfControlValue]
  )

  return (
    <LossOfControlEditorContext.Provider value={contextValue}>
      {children}
    </LossOfControlEditorContext.Provider>
  )
}

export const useLossOfControlEditor = (): LossOfControlEditorContextValue => {
  const ctx = useContext(LossOfControlEditorContext)
  if (!ctx) {
    return {
      editorMode: false,
      setEditorMode: () => {},
      lossOfControlByPlan: defaultGraphData.lossOfControlByPlan,
      setLossOfControlValue: () => {},
    }
  }
  return ctx
}
